import { calcSalaryFull } from "./salaryCalculator";

export async function runLegacySystemTests({ client, identity, log, onCompanyCreated }) {
  let testCoId, testEmpId;
      // 1. Create Company
      log("Test 1: Create Basic Company...", "running");
      const { data: coData, error: coErr } = await client.from("companies").insert({
        name: identity.name, config: { _test_automation: identity.marker },
        login_username: identity.loginPrefix + "_1",
        working_days_per_month: 26, pf_applicable: true, esi_applicable: true, absence_deduction_from: "gross", plan_tier: "Basic"
      }).select("id").single();
      
      if (coErr) throw new Error("Failed to create test company: " + coErr.message);
      testCoId = coData.id;
      onCompanyCreated(testCoId);
      log("✅ Test 1 Passed: Company Created", "pass");

      // 2. Add Employee
      log("Test 2: Add Employee (Gross 50k)...", "running");
      const { data: empData, error: empErr } = await client.from("employees").insert({
        company_id: testCoId,
        name: "Test Emp",
        employee_code: "T-01",
        basic_salary: 50000,
        date_of_joining: "2024-01-01"
      }).select("id").single();
      if (empErr) throw new Error("Failed to create employee: " + empErr.message);
      testEmpId = empData.id;
      log("✅ Test 2 Passed: Employee Created", "pass");

      // 3. Employee Delete (Single)
      log("Test 3: Delete Employee...", "running");
      const { error: delEmpErr } = await client.from("employees").delete().eq("id", testEmpId);
      if (delEmpErr) throw new Error("Failed to delete employee: " + delEmpErr.message);
      
      // Re-add employee for calculation tests
      const { data: empData2, error: empErr2 } = await client.from("employees").insert({
        company_id: testCoId, name: "Test Emp 2", employee_code: "T-02", basic_salary: 50000, date_of_joining: "2024-01-01"
      }).select("id").single();
      if (empErr2) throw new Error("Failed to recreate test employee: " + empErr2.message);
      testEmpId = empData2.id;
      log("✅ Test 3 Passed: Employee Deletion Success", "pass");

      // MATH TESTS USING calcSalaryFull
      log("Test 4: ESI Limit (Gross > 21k)...", "running");
      let cfg = { pf_applicable: true, esi_applicable: true, pf_wage_cap: true, absence_deduction: true, absence_basis: "gross" };
      let res = calcSalaryFull({ basic: 50000, month: 1, year: 2024, absent_days: 0, ot_hours: 0, cfg, emp_id: testEmpId });
      if (res.esi_emp > 0) throw new Error("ESI should be 0 when Gross > 21000");
      log("✅ Test 4 Passed: ESI properly capped at 21k", "pass");

      log("Test 5: PF Cap Applied (25k)...", "running");
      if (res.pfWage !== 25000) throw new Error(`PF Wage should be 25000, got ${res.pfWage}`);
      if (res.eps !== Math.round(25000 * 0.0833)) throw new Error(`Pension should be calculated on 25k`);
      log("✅ Test 5 Passed: PF correctly capped at 25k", "pass");

      log("Test 6: PF Cap Removed...", "running");
      cfg.pf_wage_cap = false;
      // Use gross 60,000 → computed basic = 50% × 60k = 30,000 which is > 25k cap.
      // With cap=false pfWage should equal the earned basic (30,000), not 25,000.
      res = calcSalaryFull({ basic: 60000, month: 1, year: 2024, absent_days: 0, ot_hours: 0, cfg, emp_id: testEmpId });
      if (res.pfWage <= 25000) throw new Error(`PF Wage should be > 25k when cap removed, got ${res.pfWage}. (basic input=60k → computed basic=30k)`);
      if (res.pfWage !== 30000) throw new Error(`PF Wage should be exactly 30000 (50% of 60k gross), got ${res.pfWage}`);
      log("✅ Test 6 Passed: PF calculates on full basic (30k) when cap disabled", "pass");

      log("Test 7: Pension Disabled...", "running");
      cfg.pension_applicable = false;
      res = calcSalaryFull({ basic: 10000, month: 1, year: 2024, absent_days: 0, ot_hours: 0, cfg, emp_id: testEmpId });
      if (res.eps > 0) throw new Error("Pension (EPS) should be 0 when disabled");
      if (res.epf_er !== res.pf_emp) throw new Error("Employer EPF should match Employee PF (12%) when pension disabled");
      log("✅ Test 7 Passed: 12% Employer PF goes fully to EPF when Pension is off", "pass");

      log("Test 8: Absent Deduction (Gross Basis)...", "running");
      cfg.absence_deduction = true; cfg.absence_basis = "gross"; cfg.working_days = 26;
      res = calcSalaryFull({ basic: 26000, month: 1, year: 2024, absent_days: 2, ot_hours: 0, cfg, emp_id: testEmpId });
      if (res.gross !== 24000) throw new Error(`Gross should be 24000 (26k - 2 days), got ${res.gross}`);
      log("✅ Test 8 Passed: Absent deduction correctly applied to Gross", "pass");

      log("Test 9: Absent Deduction (Basic Basis)...", "running");
      cfg.absence_basis = "basic"; cfg.da = false;
      res = calcSalaryFull({ basic: 26000, month: 1, year: 2024, absent_days: 2, ot_hours: 0, cfg, emp_id: testEmpId });
      // Basic=13000. Deduction = (13000/26)*2 = 1000.
      if (res.gross !== 26000 - 1000) throw new Error(`Gross should be 25000, got ${res.gross}`);
      log("✅ Test 9 Passed: Absent deduction applied to Basic", "pass");

      log("Test 10: OT Calculation (Gross Basis)...", "running");
      cfg.ot_applicable = true; cfg.ot_basis = "gross";
      res = calcSalaryFull({ basic: 26000, month: 1, year: 2024, absent_days: 0, ot_hours: 8, cfg, emp_id: testEmpId });
      // 8 hours = 1 day. Standard rate = 1 days worth. Gross=26000. 1 day = 1000. OT pay = 1000.
      if (res.ot_pay !== 1000) throw new Error(`OT Pay should be 1000, got ${res.ot_pay}`);
      log("✅ Test 10 Passed: OT logic applied on Gross with Standard Rate", "pass");

      log("Test 11: Zero Salary (100% Absent)...", "running");
      cfg.absence_basis = "gross";
      res = calcSalaryFull({ basic: 26000, month: 1, year: 2024, absent_days: 26, ot_hours: 0, cfg, emp_id: testEmpId });
      if (res.gross !== 0 || res.net !== 0 || res.pf_emp !== 0) throw new Error("Gross/Net/PF should be exactly 0");
      log("✅ Test 11 Passed: 100% Absent prevents negative salary", "pass");
      log("Test 12: Working Days (Custom 31 Days)...", "running");
      cfg.working_days = 31; cfg.absence_basis = "gross"; cfg.absence_deduction = true;
      res = calcSalaryFull({ basic: 31000, month: 1, year: 2024, absent_days: 1, ot_hours: 0, cfg, emp_id: testEmpId });
      // Gross 31k. 31 days. Per day = 1000. 1 day absent = 1000. Gross = 30k.
      if (res.gross !== 30000) throw new Error(`Custom 31 days failed. Gross=${res.gross}`);
      log("✅ Test 12 Passed: Custom 31 Working Days", "pass");

      log("Test 13: Working Days (Calendar/Leap Year)...", "running");
      cfg.working_days = -1; // Calendar
      // Feb 2024 is a leap year (29 days)
      res = calcSalaryFull({ basic: 29000, month: 2, year: 2024, absent_days: 1, ot_hours: 0, cfg, emp_id: testEmpId });
      if (res.gross !== 28000) throw new Error(`Leap year (Feb 2024 - 29 days) failed. Gross=${res.gross}`);
      // Feb 2023 is not a leap year (28 days)
      res = calcSalaryFull({ basic: 28000, month: 2, year: 2023, absent_days: 1, ot_hours: 0, cfg, emp_id: testEmpId });
      if (res.gross !== 27000) throw new Error(`Non-Leap year (Feb 2023 - 28 days) failed. Gross=${res.gross}`);
      log("✅ Test 13 Passed: Calendar Auto-Days (Leap Year & Normal)", "pass");

      log("Test 14: HRA Configs (40%, 50%, Fixed)...", "running");
      cfg.hra_type = "pct"; cfg.hra_pct = 50; 
      // Basic 20000. HRA = 50% = 10000. Total = 30000.
      res = calcSalaryFull({ basic: 40000, month: 1, year: 2024, absent_days: 0, ot_hours: 0, cfg, emp_id: testEmpId });
      if (res.hra !== 10000) throw new Error(`50% HRA failed. HRA=${res.hra}`);
      cfg.hra_type = "fixed"; cfg.hra_value = 8000;
      res = calcSalaryFull({ basic: 40000, month: 1, year: 2024, absent_days: 0, ot_hours: 0, cfg, emp_id: testEmpId });
      if (res.hra !== 8000) throw new Error(`Fixed HRA failed. HRA=${res.hra}`);
      log("✅ Test 14 Passed: HRA 50% and Fixed variants", "pass");

      log("Test 15: ESI Boundary Condition...", "running");
      cfg.pf_applicable = true; cfg.esi_applicable = true; cfg.da = false;
      // 42,000 Gross = 21,000 Basic. ESI is calculated on Basic under new wage code.
      let res21k = calcSalaryFull({ basic: 42000, month: 1, year: 2024, absent_days: 0, ot_hours: 0, cfg, emp_id: testEmpId });
      if (res21k.esi_emp === 0) throw new Error("ESI should calculate when Basic is exactly 21k");
      // 42,002 Gross = 21,001 Basic. Exceeds 21k ESI threshold.
      let res21k1 = calcSalaryFull({ basic: 42002, month: 1, year: 2024, absent_days: 0, ot_hours: 0, cfg, emp_id: testEmpId });
      if (res21k1.esi_emp > 0) throw new Error("ESI should be 0 when Basic is 21k + 1");
      log("✅ Test 15 Passed: ESI exactly respects 21,000 threshold on Basic", "pass");

      log("Test 16: VPF Amount Persistence & Deduction...", "running");
      const { data: vpfEmp, error: vpfError } = await client.from("employees").update({ vpf_pct: 500 }).eq("id", testEmpId).select("vpf_pct").single();
      if (vpfError) throw new Error("VPF save failed: " + vpfError.message);
      if (vpfEmp?.vpf_pct !== 500) throw new Error("VPF amount did not persist correctly.");
      const vpfCheck = calcSalaryFull({ basic: 30000, month: 1, year: 2024, cfg, extra: { vpf_enabled: true, vpf_pct: vpfEmp.vpf_pct } });
      if (vpfCheck.vpf !== 500) throw new Error("Saved VPF amount was not deducted correctly.");
      log("✅ Test 16 Passed: VPF amount saved and calculated correctly", "pass");

      log("Test 17: PF Admin Charges (Aggregation Limits)...", "running");
      // Scenario A: High wages (0.5% > 500) -> 2,00,000 * 0.005 = 1000
      let adminA = 200000 > 0 ? Math.max(Math.round(200000 * 0.005), 500) : 75;
      if (adminA !== 1000) throw new Error("Admin A failed to scale above 500");
      // Scenario B: Low wages (0.5% < 500) -> 15,000 * 0.005 = 75, so snaps to 500
      let adminB = 25000 > 0 ? Math.max(Math.round(25000 * 0.005), 500) : 75;
      if (adminB !== 500) throw new Error("Admin B failed to snap to 500 min");
      // Scenario C: Zero wages -> Non-functional 75
      let adminC = 0 > 0 ? Math.max(Math.round(0 * 0.005), 500) : 75;
      if (adminC !== 75) throw new Error("Admin C failed to hit 75 non-functional min");
      log("✅ Test 17 Passed: PF Admin charges scale and respect 500/75 minimums", "pass");

      log("Test 18: Overtime (OT) Customization & 2x Multiplier...", "running");
      const otConfig1x = { ...cfg, ot_applicable: true, ot_basis: "gross", ot_rate_multiplier: 1, working_days: 30 };
      const otRes1x = calcSalaryFull({ basic: 120000, month: 1, year: 2024, absent_days: 0, ot_hours: 10, cfg: otConfig1x });
      if (otRes1x.ot_pay !== 5000) throw new Error(`1x OT Failed. Expected 5000, got ${otRes1x.ot_pay}`);

      const otConfig2x = { ...cfg, ot_applicable: true, ot_basis: "gross", ot_rate_multiplier: 2, working_days: 30 };
      const otRes2x = calcSalaryFull({ basic: 120000, month: 1, year: 2024, absent_days: 0, ot_hours: 10, cfg: otConfig2x });
      if (otRes2x.ot_pay !== 10000) throw new Error(`2x OT Failed. Expected 10000, got ${otRes2x.ot_pay}`);
      log("✅ Test 18 Passed: OT calculates perfectly for both 1x and 2x multipliers", "pass");

      log("Test 19: Cascading Company Deletion...", "running");
      const { error: delCoErr } = await client.from("companies").delete().eq("id", testCoId);
      if (delCoErr) throw new Error("Failed to delete company: " + delCoErr.message);
      
      const { data: ghostEmp } = await client.from("employees").select("id").eq("id", testEmpId);
      if (ghostEmp && ghostEmp.length > 0) throw new Error("Employee was NOT deleted when Company was deleted (Cascade failed)");
      log("✅ Test 19 Passed: Company deletion successfully cascaded to employees", "pass");

      // ── Recreate company + employee for remaining math tests (19 deleted them)
      const { data: co2, error: co2Err } = await client.from("companies").insert({
        name: identity.name, config: { _test_automation: identity.marker }, login_username: identity.loginPrefix + "_2",
        pf_applicable: true, esi_applicable: true, plan_tier: "Basic"
      }).select("id").single();
      if (co2Err) throw new Error("Failed to recreate test company: " + co2Err.message);
      testCoId = co2.id;
      onCompanyCreated(testCoId);
      const { data: emp3, error: emp3Err } = await client.from("employees").insert({
        company_id: testCoId, name: "Math Emp", employee_code: "T-MATH",
        basic_salary: 30000, date_of_joining: "2024-01-01"
      }).select("id").single();
      if (emp3Err) throw new Error("Failed to recreate math employee: " + emp3Err.message);
      testEmpId = emp3.id;

      // ── Reset cfg for fresh tests
      cfg = { pf_applicable: true, esi_applicable: true, pf_wage_cap: true, absence_deduction: true, absence_basis: "gross", working_days: 26 };

      // Test 20: Professional Tax — below ₹25k gross = ₹200 PT (non-Feb), Feb = ₹300 at ₹25k+
      log("Test 20: Professional Tax Slabs...", "running");
      let ptLow = calcSalaryFull({ basic: 20000, month: 3, year: 2024, absent_days: 0, ot_hours: 0, cfg });
      if (ptLow.pt !== 0) throw new Error(`PT should be 0 for gross < 25k, got ${ptLow.pt}`);
      let ptHigh = calcSalaryFull({ basic: 50000, month: 3, year: 2024, absent_days: 0, ot_hours: 0, cfg });
      if (ptHigh.pt !== 200) throw new Error(`PT should be ₹200 for gross >= 25k (non-Feb), got ${ptHigh.pt}`);
      let ptFeb = calcSalaryFull({ basic: 50000, month: 2, year: 2024, absent_days: 0, ot_hours: 0, cfg });
      if (ptFeb.pt !== 300) throw new Error(`PT should be ₹300 in February for gross >= 25k, got ${ptFeb.pt}`);
      log("✅ Test 20 Passed: Professional Tax slabs (₹0 / ₹200 / ₹300 Feb) correct", "pass");

      // Test 21: Net Pay Integrity — Net = Gross + OT - PF(emp) - ESI(emp) - PT - TDS - VPF
      log("Test 21: Net Pay Integrity...", "running");
      let netRes = calcSalaryFull({ basic: 30000, month: 1, year: 2024, absent_days: 0, ot_hours: 0, cfg });
      const expectedNet = netRes.gross - netRes.pf_emp - netRes.esi_emp - netRes.pt - (netRes.tds || 0) - (netRes.vpf || 0) + (netRes.ot_pay || 0);
      if (netRes.net !== expectedNet) throw new Error(`Net pay mismatch. Expected ${expectedNet}, got ${netRes.net}`);
      log("✅ Test 21 Passed: Net = Gross − statutory deductions", "pass");

      // Test 22: TDS flat deduction
      log("Test 22: TDS Flat Deduction...", "running");
      const tdsCfg = { ...cfg, tds_applicable: true };
      let tdsRes = calcSalaryFull({ basic: 50000, month: 1, year: 2024, absent_days: 0, ot_hours: 0, cfg: tdsCfg, extra: { tds_amount: 5000 } });
      if (tdsRes.tds !== 5000) throw new Error(`TDS should be 5000, got ${tdsRes.tds}`);
      if (tdsRes.net !== tdsRes.gross - tdsRes.pf_emp - tdsRes.esi_emp - tdsRes.pt - 5000) throw new Error("TDS not subtracted from net correctly");
      log("✅ Test 22 Passed: TDS flat amount deducted from net", "pass");

      // Test 23: Minimum Wage Floor — basic+DA cannot go below floor
      log("Test 23: Minimum Wage Floor...", "running");
      const mwCfg = { ...cfg, min_wage_enabled: true, min_wage: 15000 };
      let mwRes = calcSalaryFull({ basic: 10000, month: 1, year: 2024, absent_days: 0, ot_hours: 0, cfg: mwCfg });
      // computed basic = 50% of 10k = 5000, but floor kicks in at 15000
      if ((mwRes.basic) < 15000) throw new Error(`Min wage floor failed. Basic+DA should be >= 15000, got ${mwRes.basic}`);
      log("✅ Test 23 Passed: Minimum wage floor applied to Basic", "pass");

      // Test 24: VPF extra deduction reduces net
      log("Test 24: VPF Extra Deduction...", "running");
      const vpfCfg = { ...cfg };
      let baseRes = calcSalaryFull({ basic: 30000, month: 1, year: 2024, absent_days: 0, ot_hours: 0, cfg: vpfCfg, extra: {} });
      let vpfRes  = calcSalaryFull({ basic: 30000, month: 1, year: 2024, absent_days: 0, ot_hours: 0, cfg: vpfCfg, extra: { vpf_enabled: true, vpf_pct: 5 } });
      if (vpfRes.vpf <= 0) throw new Error(`VPF should be > 0 when vpf_pct=5, got ${vpfRes.vpf}`);
      if (vpfRes.net >= baseRes.net) throw new Error(`Net with VPF (${vpfRes.net}) should be less than net without VPF (${baseRes.net})`);
      log("✅ Test 24 Passed: VPF deducted and reduces net pay", "pass");

      // Test 25: Salary Record DB upsert + conflict resolution
      log("Test 25: Salary Record Upsert (DB)...", "running");
      const salPayload = { employee_id: testEmpId, company_id: testCoId, month: 6, year: 2024, basic: 15000, net_pay: 13000, status: "draft" };
      const { error: salErr1 } = await client.from("salary_records").upsert(salPayload, { onConflict: "employee_id,month,year" });
      if (salErr1) throw new Error("Salary upsert (insert) failed: " + salErr1.message);
      // Re-upsert with different net — should update not duplicate
      const { error: salErr2 } = await client.from("salary_records").upsert({ ...salPayload, net_pay: 13500 }, { onConflict: "employee_id,month,year" });
      if (salErr2) throw new Error("Salary upsert (update) failed: " + salErr2.message);
      const { data: salCheck } = await client.from("salary_records").select("net_pay").eq("employee_id", testEmpId).eq("month", 6).eq("year", 2024);
      if (!salCheck || salCheck.length !== 1) throw new Error(`Expected 1 salary record, got ${salCheck?.length}`);
      if (salCheck[0].net_pay !== 13500) throw new Error(`Upsert should update net_pay to 13500, got ${salCheck[0].net_pay}`);
      log("✅ Test 25 Passed: Salary upsert correctly inserts then updates (no duplicates)", "pass");

      // Test 26: Multi-month payroll history — 3 months stored and retrieved
      log("Test 26: Multi-Month Payroll History (DB)...", "running");
      const months = [{ m: 4, y: 2024, net: 12000 }, { m: 5, y: 2024, net: 12500 }, { m: 7, y: 2024, net: 11800 }];
      for (const row of months) {
        const { error: mErr } = await client.from("salary_records").upsert(
          { employee_id: testEmpId, company_id: testCoId, month: row.m, year: row.y, basic: 15000, net_pay: row.net, status: "draft" },
          { onConflict: "employee_id,month,year" }
        );
        if (mErr) throw new Error(`Multi-month insert failed for month ${row.m}: ${mErr.message}`);
      }
      const { data: historyRows } = await client.from("salary_records").select("month,net_pay").eq("employee_id", testEmpId).eq("year", 2024);
      // Should have at least 4 rows now (month 6 from T25 + 3 new)
      if (!historyRows || historyRows.length < 4) throw new Error(`Expected >= 4 salary history rows, got ${historyRows?.length}`);
      log("✅ Test 26 Passed: Multi-month salary history stored and retrieved correctly", "pass");

      // Test 27: Verify persisted status; workflow write protection is checked by the shared regressions.
      log("Test 27: Payroll Lock Status...", "running");
      await client.from("salary_records").update({ status: "locked" }).eq("employee_id", testEmpId).eq("month", 6).eq("year", 2024);
      const { data: lockedRow } = await client.from("salary_records").select("status").eq("employee_id", testEmpId).eq("month", 6).eq("year", 2024).single();
      if (lockedRow?.status !== "locked") throw new Error(`Status should be 'locked', got '${lockedRow?.status}'`);
      log("✅ Test 27 Passed: Salary record lock status persisted correctly", "pass");

      // Test 28: Gross = 0 edge case — all deductions must also be 0
      log("Test 28: Zero Gross Edge Case...", "running");
      let zeroRes = calcSalaryFull({ basic: 0, month: 1, year: 2024, absent_days: 0, ot_hours: 0, cfg });
      if (zeroRes.gross !== 0) throw new Error(`Gross should be 0, got ${zeroRes.gross}`);
      if (zeroRes.pf_emp !== 0) throw new Error(`PF should be 0 when gross=0, got ${zeroRes.pf_emp}`);
      if (zeroRes.esi_emp !== 0) throw new Error(`ESI should be 0 when gross=0, got ${zeroRes.esi_emp}`);
      if (zeroRes.net !== 0) throw new Error(`Net should be 0 when gross=0, got ${zeroRes.net}`);
      log("✅ Test 28 Passed: Zero gross produces zero deductions and zero net", "pass");

}
